import { Alert, Button, Typography } from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import { showError, showSuccess, showWarning } from "common/ToastNotifications";
import AIButton from "components/molecules/AIButton";
import DatasetDescriptionModal from "components/molecules/DatasetDescriptionModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { getDatasetDescriptionRequest, resetDatasetDescription } from "store/dataObjects/actions";
import { getDataSetFieldInsightsRequest, getDataSetJobsRequest, runDataSetProfilingRequest } from "store/dataSets/actions";

const { Title, Paragraph } = Typography;

// Resolve the businessAreaId/schemaId needed by the AI describe endpoint —
// prefer the loaded dataset entity, fall back to the field-insights records.
function resolveContext(dataSet, insightsItems, dataSetId) {
    if (dataSet && dataSet.dataSetId === dataSetId && dataSet.businessAreaId && dataSet.schemaId) {
        return { businessAreaId: dataSet.businessAreaId, schemaId: dataSet.schemaId };
    }
    const match = (insightsItems || []).find((item) => item && item.businessAreaId && item.schemaId);
    return match
        ? { businessAreaId: match.businessAreaId, schemaId: match.schemaId }
        : { businessAreaId: null, schemaId: null };
}

// True while a profiling job for this dataset is queued/running. The jobs list is kept live
// by the notifications WebSocket (DATA_SET_JOB_EXECUTION_PROGRESS), so this reflects real state.
function hasActiveProfilingJob(jobsResult) {
    const jobs = jobsResult?.Items || [];
    return jobs.some(
        (job) =>
            job.jobType === "PROFILING" &&
            (job.status === "RUNNING" || job.status === "PENDING" || job.status === "EXEC"),
    );
}

function hasCompletedProfilingJob(jobsResult) {
    const jobs = jobsResult?.Items || [];
    return jobs.some((job) => job.jobType === "PROFILING" && job.status === "COMPLETED");
}

function DataSetAskAIContent({
    dataSetId,
    dataSet,
    dataSetFieldInsightsResult,
    datasetDescription,
    datasetDescriptionLoading,
    getDatasetDescriptionResult,
    runDataSetProfilingResult,
    profilingJobRunning,
    jobsResult,
    getDataSetFieldInsights,
    getDataSetJobs,
    runDataSetProfiling,
    getDatasetDescription,
    resetDatasetDescription,
}) {
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (!dataSetId) return;
        getDataSetFieldInsights(dataSetId);
        getDataSetJobs(dataSetId);
        // Clear a stale summary left over from a different dataset.
        if (datasetDescription && datasetDescription.dataSetId && datasetDescription.dataSetId !== dataSetId) {
            resetDatasetDescription();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSetId]);

    const insightsItems = dataSetFieldInsightsResult?.Items || [];
    const profilingAvailable = useMemo(() => {
        const items = dataSetFieldInsightsResult?.Items || [];
        return items.some((item) => item && item.profilingStats && Object.keys(item.profilingStats).length > 0);
    }, [dataSetFieldInsightsResult]);

    const profilingJobActive = profilingJobRunning || hasActiveProfilingJob(jobsResult);

    // Feedback for the profiling submit (mirrors the Home dashboard "Run Profiling" entry point).
    const prevProfilingJobRunning = useRef(profilingJobRunning);
    const prevRunProfilingResult = useRef(runDataSetProfilingResult);
    useEffect(() => {
        if (runDataSetProfilingResult !== prevRunProfilingResult.current) {
            if (!runDataSetProfilingResult.success) {
                if (runDataSetProfilingResult.code === "JOB_ALREADY_RUNNING") {
                    showWarning("A profiling job is already in progress for this dataset.");
                } else {
                    showError("Unable to start profiling job.");
                }
            } else if (prevProfilingJobRunning.current && !profilingJobRunning) {
                showSuccess("Profiling job submitted. The AI summary will enable automatically once it completes.");
                if (dataSetId) getDataSetJobs(dataSetId);
            }
        }
        prevProfilingJobRunning.current = profilingJobRunning;
        prevRunProfilingResult.current = runDataSetProfilingResult;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runDataSetProfilingResult, profilingJobRunning]);

    // Event-driven completion: the profiling worker -> job-service completeJob() sends a
    // JOB_EXECUTION_PROGRESS notification over the WebSocket with status COMPLETED, which the
    // notifications saga dispatches into jobsResult. When a PROFILING job transitions into
    // COMPLETED, refetch field insights once so profilingStats land and the AI Summary button
    // enables itself — no navigation, no polling.
    const prevHadCompletedProfiling = useRef(hasCompletedProfilingJob(jobsResult));
    useEffect(() => {
        const nowCompleted = hasCompletedProfilingJob(jobsResult);
        if (nowCompleted && !prevHadCompletedProfiling.current && dataSetId && !profilingAvailable) {
            getDataSetFieldInsights(dataSetId);
        }
        prevHadCompletedProfiling.current = nowCompleted;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobsResult, profilingAvailable, dataSetId]);

    const ctx = resolveContext(dataSet, insightsItems, dataSetId);
    const canGenerate = Boolean(profilingAvailable && dataSetId && ctx.businessAreaId && ctx.schemaId);

    const isForThisDataset = datasetDescription && datasetDescription.dataSetId === dataSetId;
    const summary = isForThisDataset ? datasetDescription : null;
    const hasSummary = Boolean(summary && (summary.reportHtml || summary.description));
    const hasError = getDatasetDescriptionResult && getDatasetDescriptionResult.success === false;

    const generate = (regenerate) => {
        if (ctx.businessAreaId && ctx.schemaId && dataSetId) {
            getDatasetDescription(ctx.businessAreaId, ctx.schemaId, dataSetId, regenerate);
        }
    };

    const handleOpen = () => {
        setModalOpen(true);
        // Generate on open only if we don't already have a summary for this dataset.
        if (!hasSummary && !datasetDescriptionLoading && canGenerate) {
            generate(false);
        }
    };

    const handleRunProfiling = () => {
        if (dataSetId && !profilingJobActive) {
            runDataSetProfiling(dataSetId);
        }
    };

    const disabledReason = !dataSetId
        ? "Open a dataset in view or edit mode."
        : !profilingAvailable
          ? "Run dataset profiling first to enable the AI summary."
          : !canGenerate
            ? "Dataset context is unavailable. Re-open the dataset and try again."
            : "";

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 bg-white p-4">
            <div className="space-y-2">
                <Title level={5} className="!mb-0">
                    Dataset AI Tools
                </Title>
                <Paragraph className="!mb-0 text-sm text-gray-600">
                    Generate an AI summary and data-quality assessment grounded in this dataset's profiling statistics.
                </Paragraph>
            </div>

            {!profilingAvailable && dataSetId && (
                <Alert
                    type="info"
                    showIcon
                    message={profilingJobActive ? "Profiling in progress" : "AI summary needs profiling"}
                    description={
                        profilingJobActive
                            ? "The AI summary will enable automatically once profiling completes."
                            : "Run profiling on this dataset to enable the AI summary."
                    }
                    action={
                        <Button
                            size="small"
                            type="primary"
                            icon={<ExperimentOutlined />}
                            loading={profilingJobActive}
                            disabled={!dataSetId || profilingJobActive}
                            onClick={handleRunProfiling}
                        >
                            {profilingJobActive ? "Profiling..." : "Run Profiling"}
                        </Button>
                    }
                />
            )}

            <div className="grid gap-3">
                <AIButton
                    onClick={handleOpen}
                    disabled={!canGenerate}
                    loading={datasetDescriptionLoading}
                    tooltipTitle="Generate an AI summary and data-quality assessment for this dataset"
                    disabledTooltipTitle={disabledReason}
                    buttonText="AI Summary"
                />
            </div>

            <DatasetDescriptionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                summary={summary}
                loading={datasetDescriptionLoading}
                error={hasError}
                errorMessage={getDatasetDescriptionResult?.message}
                onRegenerate={() => generate(true)}
            />
        </div>
    );
}

const mapStateToProps = (state) => ({
    dataSet: state.dataSets.dataSet,
    dataSetFieldInsightsResult: state.dataSets.dataSetFieldInsightsResult,
    datasetDescription: state.dataObjects.datasetDescription,
    datasetDescriptionLoading: state.dataObjects.datasetDescriptionLoading,
    getDatasetDescriptionResult: state.dataObjects.getDatasetDescriptionResult,
    runDataSetProfilingResult: state.dataSets.runDataSetProfilingResult,
    profilingJobRunning: state.dataSets.profilingJobRunning,
    jobsResult: state.dataSets.jobsResult,
});

const mapDispatchToProps = (dispatch) => ({
    getDataSetFieldInsights: (dataSetId) => dispatch(getDataSetFieldInsightsRequest(dataSetId)),
    getDataSetJobs: (dataSetId) => dispatch(getDataSetJobsRequest(dataSetId)),
    runDataSetProfiling: (dataSetId) => dispatch(runDataSetProfilingRequest(dataSetId)),
    getDatasetDescription: (businessAreaId, schemaId, dataSetId, regenerate) =>
        dispatch(getDatasetDescriptionRequest(businessAreaId, schemaId, dataSetId, regenerate)),
    resetDatasetDescription: () => dispatch(resetDatasetDescription()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataSetAskAIContent);
